import './index.css'
import './locale/i18n'

import * as Sentry from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'

import { getConfig } from '@/api/config'
import { getUser } from '@/api/user'
import { ConfigProvider } from '@/contexts/config'
import { Error } from '@/pages/Error'
import { Root } from '@/pages/Root'
import { User } from '@/types/user'

const router = createBrowserRouter([
  /*
    URLS format:
      http://go.abi.ai/<parnerKey>/<userId>?q=<consultationType>
      http://go.abi.ai/<partnerKey>?q=<consultationType>

  */
  {
    path: '/:key/:userId?',
    element: <Root />,
    errorElement: (
      <ConfigProvider>
        <Error />
      </ConfigProvider>
    ),
    loader: async ({ params: { key, userId } }) => {
      Sentry.setTag('apiKey', key)

      if (!key) throw new Response('Not Found', { status: 404 })

      const { config } = await getConfig()
      localStorage.setItem('config', JSON.stringify(config))

      const lsUser = localStorage.getItem('user')
      const user = lsUser ? (JSON.parse(lsUser) as User) : null

      if (userId && (!user || user.data?.userId !== userId)) {
        const user = await getUser({ userId })

        localStorage.setItem('user', JSON.stringify(user))
        if (user.data?.language) localStorage.setItem('languageSelected', user.data?.language)
      }

      return null
    }
  },
  {
    path: '/',
    element: (
      <ConfigProvider>
        <Error />
      </ConfigProvider>
    )
  },
  {
    path: '/error',
    element: (
      <ConfigProvider>
        <Error />
      </ConfigProvider>
    )
  }
])

// Do not initialize Sentry on localhost
if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
  // This is needed in order to difference between canary and prod (both are same .env)
  let environment
  if (import.meta.env.DEV || window.location.hostname.startsWith('dev')) {
    environment = 'development'
  } else if (window.location.hostname.startsWith('test')) {
    environment = 'test'
  } else if (window.location.hostname.startsWith('canary')) {
    environment = 'canary'
  } else {
    environment = 'production'
  }

  const sentryConfig: Sentry.BrowserOptions = {
    dsn: 'https://c9aff78a5aa946b593df971cd48e6220@o509172.ingest.sentry.io/4504729837174784',
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
    environment,
    release: import.meta.env.RELEASE,
    initialScope: {
      tags: {
        version: import.meta.env.APP_VERSION,
        buildDate: import.meta.env.BUILD_DATE
      }
    },
    ignoreErrors: [/Network Error/i, /Failed to fetch/i] // Ignore errors caused by network issues
  }

  Sentry.init(sentryConfig)
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(<RouterProvider router={router} />)
